// Filters and display order travel with the board's address.
import type { Priority } from '../api/types'
import { PRIORITY_ORDER } from '../vocab'

export const LIVE_FILTERS = ['working', 'needs', 'ready', 'deploy', 'stuck'] as const
export type LiveFilter = typeof LIVE_FILTERS[number]
export type SortMode = 'manual' | 'priority' | 'updated' | 'created'
export const SORT_MODES: readonly { value: SortMode; name: string }[] = [
  { value: 'manual', name: 'Manual' }, { value: 'priority', name: 'Priority' },
  { value: 'updated', name: 'Updated' }, { value: 'created', name: 'Created' },
]
export interface BoardView {
  live: LiveFilter | null
  q: string
  labels: readonly string[]
  priorities: readonly Priority[]
  claimed: string
  sort: SortMode
  /** The columns shown whole, not the last days: a terminal state's slug, or the id of a column of the project's own. */
  showAll: readonly string[]
  closed: boolean
  /** A plan's id: the board shows the issues it lists (today's `?plan=`). */
  plan: string | null
}
export const DEFAULT_VIEW: BoardView = { live: null, q: '', labels: [], priorities: [], claimed: '', sort: 'manual', showAll: [], closed: false, plan: null }
const list = (params: URLSearchParams, key: string) => (params.get(key) ?? '').split(',').map(s => s.trim()).filter(Boolean)
export function parseView(params: URLSearchParams): BoardView {
  const live = params.get('live') as LiveFilter
  const sort = params.get('sort') as SortMode
  return {
    live: LIVE_FILTERS.includes(live) ? live : null,
    q: params.get('q') ?? '',
    labels: [...new Set([...list(params, 'label'), ...params.getAll('labels')].map(s => s.trim().toLowerCase()).filter(Boolean))],
    priorities: [...new Set(list(params, 'priority').filter((s): s is Priority => PRIORITY_ORDER.includes(s as Priority)))],
    claimed: params.get('claimed') ?? '',
    sort: SORT_MODES.some(m => m.value === sort) ? sort : 'manual',
    showAll: list(params, 'all').filter(s => ['done', 'cancelled', 'duplicate'].includes(s) || /^[0-9a-f-]{36}$/.test(s)),
    closed: params.get('closed') === '1',
    plan: params.get('plan') || null,
  }
}
export function viewToParams(view: BoardView): URLSearchParams {
  const params = new URLSearchParams()
  if (view.live) params.set('live', view.live)
  if (view.q) params.set('q', view.q)
  for (const label of view.labels) params.append('labels', label)
  if (view.priorities.length) params.set('priority', view.priorities.join(','))
  if (view.claimed) params.set('claimed', view.claimed)
  if (view.sort !== 'manual') params.set('sort', view.sort)
  if (view.showAll.length) params.set('all', view.showAll.join(','))
  if (view.closed) params.set('closed', '1')
  if (view.plan) params.set('plan', view.plan)
  return params
}
export function hasFilters(view: BoardView): boolean {
  return Boolean(view.live || view.q.trim() || view.labels.length || view.priorities.length || view.claimed || view.plan)
}
export function clearFilters(view: BoardView): BoardView {
  return { ...view, live: null, q: '', labels: [], priorities: [], claimed: '', plan: null }
}
export function toggle<T>(values: readonly T[], value: T): T[] {
  return values.includes(value) ? values.filter(v => v !== value) : [...values, value]
}
